import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import './PromoCodes.css';
import { PromoCodeType, PromoCodesProps } from '../../types/promo.types';
import { CODE_PROMO } from '../../mocks/PromoCodes.mocks';

// Minimum width for a single promo item (px), used to decide how many fit
const ITEM_MIN_WIDTH = 80;
// Gap between items (px), must match CSS gap
const ITEM_GAP = 8;
// Width consumed by nav buttons + gaps (2 × 32px button + 3 × 8px gap)
const NAV_RESERVED = 2 * 32 + 3 * ITEM_GAP;

function calcVisibleCount(availableWidth: number): number {
  // Solve: n * ITEM_MIN_WIDTH + (n - 1) * ITEM_GAP <= availableWidth
  // => n <= (availableWidth + ITEM_GAP) / (ITEM_MIN_WIDTH + ITEM_GAP)
  const n = Math.floor((availableWidth + ITEM_GAP) / (ITEM_MIN_WIDTH + ITEM_GAP));
  return Math.min(4, Math.max(1, n));
}

export const PromoCodes: React.FC<PromoCodesProps> = ({
  view,
  selectedPromo,
  setSelectedPromo,
  setShowPromoCodeModal,
  promoCodes
}) => {
  const [bottomPromoStartIndex, setBottomPromoStartIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateVisibleCount = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const gridWidth = containerWidth - NAV_RESERVED;
    const count = calcVisibleCount(gridWidth);
    setVisibleCount(count);
  }, []);

  useEffect(() => {
    updateVisibleCount();
    const ro = new (window as any).ResizeObserver(updateVisibleCount);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [updateVisibleCount]);

  // Clamp start index when visible count changes
  useEffect(() => {
    setBottomPromoStartIndex(prev =>
      Math.min(prev, Math.max(0, promoCodes.length - visibleCount))
    );
  }, [visibleCount, promoCodes.length]);

  return (
    <div className="promo-codes-container" ref={containerRef}>
      {view === 'sale' ? (
        <>
          <button
            type="button"
            onClick={() => setBottomPromoStartIndex(prev => Math.max(0, prev - 1))}
            disabled={bottomPromoStartIndex === 0}
            className="promo-codes-nav-btn"
            title="Promoción Anterior"
          >
            <ChevronLeft size={16} />
          </button>

          <div
            className="promo-codes-grid"
            style={{ gridTemplateColumns: `repeat(${visibleCount}, minmax(0, 1fr))` }}
          >
            {promoCodes.slice(bottomPromoStartIndex, bottomPromoStartIndex + visibleCount).map(p => {
              const isSelected = selectedPromo?.code === p.code;
              return (
                <button
                  key={p.code}
                  onClick={() => {
                    if (p.code === CODE_PROMO) {
                      if (isSelected) {
                        setSelectedPromo(null);
                      } else {
                        setShowPromoCodeModal(true);
                      }
                    } else {
                      setSelectedPromo(isSelected ? null : p);
                    }
                  }}
                  type="button"
                  className={`promo-codes-item ${isSelected ? 'promo-codes-item-selected' : ''}`}
                >
                  <div className="promo-codes-item-header">
                    <Sparkles className="promo-codes-item-icon" size={12} />
                    <span className="promo-codes-item-label">{p.label.split('(')[0].trim()}</span>
                  </div>
                  <span className="promo-codes-item-discount">
                    -{p.discountPercent}%
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setBottomPromoStartIndex(prev => Math.min(promoCodes.length - visibleCount, prev + 1))}
            disabled={bottomPromoStartIndex + visibleCount >= promoCodes.length}
            className="promo-codes-nav-btn"
            title="Siguiente Promoción"
          >
            <ChevronRight size={16} />
          </button>
        </>
      ) : (
        <div className="promo-codes-not-applicable">
          Promociones no aplicables
        </div>
      )}
    </div>
  );
};
